import React, { useMemo } from "react";

import type { OddsFormat } from "types/BetTypes";
import type { SelectionOptions } from "features/entries/types/selections";
import type { ProcessedSingleSelection } from "@/hooks/firestore/betting/useEntries";

import { titleCase } from "title-case";
import { BetOddsWithDeductions } from "@/features/entries/components/BetOddsWithDeductions";
import { TitleIcon } from "@/features/shell/components/shared/TitleIcon";
import {
  getSelectionParentName,
  isSelectionSP,
  selectionToUrl,
} from "@/features/entries/util/selection-helpers";
import { useTitle } from "@/hooks/graphql/useTitles";
import { formatUserDateTime } from "@/utilities/datetime";
import { SELECTION_DATETIME } from "@/utilities/dateTimeFormats";
import { usePreferences } from "@/hooks/firestore/user/usePreferences";
import { getSelectionClickHandler } from "@/features/entries/util/selection-helpers";
import { DrawerLineItem } from "../common/DrawerLineItem";
import { TeamNames } from "../common/TeamNames";
import { SelectionClamp } from "./SelectionClamp";
import { Selection } from "./BaseSelection";
import { SelectionIcon } from "./SelectionIcon";

interface SingleSelectionProps {
  entryId: number;
  selection: ProcessedSingleSelection;
  isShared: boolean;
  isPreview?: boolean;
  options: SelectionOptions;
  createdAt: string;
}

const SingleSelection = ({
  entryId,
  selection,
  isShared,
  isPreview,
  options,
  createdAt,
}: SingleSelectionProps) => {
  const title = useTitle(selection.event.sport);
  const oddsFormat = usePreferences()?.preferences?.oddsFormat?.toLowerCase();

  const url = selectionToUrl(selection);
  const parentName = getSelectionParentName(selection);

  const isMatch = selection.event.eventType === "MATCH";

  const isSP = useMemo(() => {
    return isSelectionSP(selection);
  }, [selection.odds]);

  return (
    <Selection
      isCashedOut={options.isCashedOut}
      isFirst={options.isFirst}
      isLast={options.isLast}
      isVoid={options.isVoid}
      isSelectionWon={options.isSelectionWon}
      isEntryWon={options.isEntryWon}
      isCombo={options.isCombo}
      isDecided={options.isDecided}
      connectUp={options.connectUp}
      connectDown={options.connectDown}
      onClick={getSelectionClickHandler(selection)}
      slots={{
        icon: <TitleIcon title={title} />,
        outcomeName: (
          <>
            <SelectionIcon selection={selection} /> {selection.outcomeName}
          </>
        ),
        clamp: (
          <SelectionClamp
            selection={selection}
            isPreview={isPreview}
            isEntryWon={options.isEntryWon}
          />
        ),
        marketName: `${selection.marketName}`,
        odds: isSP ? (
          "@ Starting Price"
        ) : (
          <BetOddsWithDeductions
            prefix={"@"}
            base={selection.odds}
            format={oddsFormat as OddsFormat}
            settlementOdds={selection.settlementOdds}
            deadHeatFactor={selection.deadHeatFactor}
            deductions={selection.deductions}
            isVoid={options.isVoid}
            pickUrl={url}
          />
        ),
        drawerContent: (
          <>
            <DrawerLineItem
              label="Start"
              value={formatUserDateTime(
                selection.event.scheduledStartTime!,
                SELECTION_DATETIME,
              )}
            />

            {isMatch ? (
              <DrawerLineItem
                label="Match"
                value={<TeamNames eventName={selection.event.eventName} />}
              />
            ) : (
              <DrawerLineItem
                label="Event"
                value={titleCase(
                  selection.event.eventName?.toLowerCase() ?? "",
                )}
              />
            )}

            {parentName && <DrawerLineItem label="" value={parentName} />}

            <DrawerLineItem
              label="Placed"
              value={formatUserDateTime(createdAt, SELECTION_DATETIME)}
            />

            {!isShared && (
              <DrawerLineItem label="Entry Id" value={`B-${entryId}`} />
            )}
          </>
        ),
      }}
    />
  );
};

export { SingleSelection };
